import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, TextField } from '@mui/material';
import UseCaseLink from './UseCaseLink.tsx';
import GradientText from './common/GradientText.tsx';

interface DroppedUseCase {
  id: string;
  icon: string;
  text: string;
  position: number;
  columnIndex: number;
}

interface ColumnTitle {
  id: number;
  title: string;
}

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '85vh',
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(4),
  color: '#F5F5F5',
  zIndex: 1,
  background: 'rgba(26, 26, 46, 0.25)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
}));

const BuildingArea = styled(Box)(({ theme }) => ({
  flex: '1',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  position: 'relative',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    animation: 'gradient 3s linear infinite',
  },
}));

const ColumnTitles = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ColumnTitleBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  background: 'rgba(138, 43, 226, 0.15)',
  backdropFilter: 'blur(3px)',
  borderRadius: theme.spacing(1),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(138, 43, 226, 0.3)',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.25)',
    transform: 'translateY(-2px)',
  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(2),
  flex: 1,
}));

const DropZone = styled(Box)(({ theme }) => ({
  height: '80px',
  width: '210px',
  background: 'rgba(138, 43, 226, 0.08)',
  backdropFilter: 'blur(3px)',
  borderRadius: theme.spacing(2),
  border: '2px dashed rgba(0, 255, 255, 0.3)',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: 'rotate(-30deg) skew(25deg) translate(0,0)',
  position: 'relative',
  margin: '40px auto',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '10px',
    left: '-20px',
    height: '100%',
    width: '20px',
    background: 'rgba(138, 43, 226, 0.05)',
    transform: 'rotate(0deg) skewY(-45deg)',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-20px',
    left: '-10px',
    height: '20px',
    width: '100%',
    background: 'rgba(138, 43, 226, 0.05)',
    transform: 'rotate(0deg) skewX(-45deg)',
  },

  '&.drag-over': {
    background: 'rgba(0, 204, 153, 0.1)',
    borderColor: '#00CC99',
    transform: 'rotate(-30deg) skew(25deg) translate(0,0) scale(1.02)',
  },
}));

const TemplateTray = styled(Box)(({ theme }) => ({
  width: '300px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.3)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  marginLeft: theme.spacing(4),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
}));

const BuilderContainer: React.FC = () => {
  const [droppedUseCases, setDroppedUseCases] = useState<DroppedUseCase[]>(() => {
    const saved = localStorage.getItem('droppedUseCases');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnTitles, setColumnTitles] = useState<ColumnTitle[]>(() => {
    const saved = localStorage.getItem('columnTitles');
    return saved ? JSON.parse(saved) : [
      { id: 0, title: 'Theme' },
      { id: 1, title: 'Initiative' },
      { id: 2, title: 'Epic' },
      { id: 3, title: 'Sprint' }
    ];
  });

  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);

  // Save state to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('droppedUseCases', JSON.stringify(droppedUseCases));
    localStorage.setItem('columnTitles', JSON.stringify(columnTitles));
  }, [droppedUseCases, columnTitles]);

  const handleDragOver = (e: React.DragEvent, position: number, columnIndex: number) => {
    e.preventDefault();
    const dropZone = e.currentTarget as HTMLElement;
    dropZone.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    const dropZone = e.currentTarget as HTMLElement;
    dropZone.classList.remove('drag-over');
  };

  const handleDrop = (e: React.DragEvent, position: number, columnIndex: number) => {
    e.preventDefault();
    const dropZone = e.currentTarget as HTMLElement;
    dropZone.classList.remove('drag-over');

    const useCaseData = e.dataTransfer.getData('application/usecase');
    if (useCaseData) {
      const useCase = JSON.parse(useCaseData);
      
      // Check if this is a move operation (existing use case)
      const existingIndex = droppedUseCases.findIndex(uc => uc.id === useCase.id);
      
      setDroppedUseCases(prev => {
        const newCases = [...prev];
        if (existingIndex >= 0) {
          // Move operation
          newCases.splice(existingIndex, 1);
        }
        
        // Add to new position
        const targetPosition = position + (columnIndex * 3);
        newCases.push({ 
          ...useCase, 
          position: targetPosition,
          columnIndex 
        });
        
        return newCases.sort((a, b) => a.position - b.position);
      });
    }
  };

  const handleDelete = (id: string) => {
    setDroppedUseCases(prev => prev.filter(uc => uc.id !== id));
  };

  const handleUpdate = (id: string, newText: string) => {
    setDroppedUseCases(prev => 
      prev.map(uc => uc.id === id ? { ...uc, text: newText } : uc)
    );
  };

  const handleTitleClick = (id: number) => {
    setEditingTitleId(id);
  };

  const handleTitleChange = (id: number, newTitle: string) => {
    setColumnTitles(prev => 
      prev.map(title => 
        title.id === id ? { ...title, title: newTitle } : title
      )
    );
    setEditingTitleId(null);
  };

  const templateUseCases = [
    { id: 'template1', icon: 'key', text: 'Automation' },
    { id: 'template2', icon: 'idea', text: 'Innovation' },
    { id: 'template3', icon: 'alert', text: 'Security' },
  ];

  return (
    <Container>
      <BuildingArea>
        <ColumnTitles>
          {columnTitles.map((col) => (
            <ColumnTitleBox key={col.id} onClick={() => handleTitleClick(col.id)}>
              {editingTitleId === col.id ? (
                <TextField
                  value={col.title}
                  onChange={(e) => handleTitleChange(col.id, e.target.value)}
                  onBlur={() => setEditingTitleId(null)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTitleChange(col.id, (e.target as HTMLInputElement).value)}
                  autoFocus
                  variant="standard"
                  sx={{
                    input: {
                      color: '#fff',
                      textAlign: 'center',
                      fontSize: '1.1rem',
                    }
                  }}
                />
              ) : (
                <GradientText  
                  sx={{ 
                    fontWeight: 800, 
                    letterSpacing: '0.5px',
                  }}
                >
                  {col.title}
                </GradientText>
              )}
            </ColumnTitleBox>
          ))}
        </ColumnTitles>

        <GridContainer>
          {columnTitles.map((column, columnIndex) => (
            <Box key={column.id} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 3 }).map((_, rowIndex) => {
                const position = rowIndex + (columnIndex * 3);
                const droppedUseCase = droppedUseCases.find(uc => 
                  uc.position === position && uc.columnIndex === columnIndex
                );
                
                return droppedUseCase ? (
                  <UseCaseLink
                    key={droppedUseCase.id}
                    id={droppedUseCase.id}
                    icon={droppedUseCase.icon}
                    text={droppedUseCase.text}
                    isTemplate={false}
                    isDraggable={true}
                    isPlaced={true}
                    onDelete={handleDelete}
                    onUpdate={handleUpdate}
                  />
                ) : (
                  <DropZone
                    key={`${columnIndex}-${rowIndex}`}
                    onDragOver={(e) => handleDragOver(e, position, columnIndex)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, position, columnIndex)}
                  >
                    <GradientText  
                      sx={{ 
                        fontWeight: 200, 
                        letterSpacing: '0.5px',
                      }}
                    >
                      Drop Use Case Here
                    </GradientText>
                  </DropZone>
                );
              })}
            </Box>
          ))}
        </GridContainer>
      </BuildingArea>

      <TemplateTray>
        <GradientText variant='h6' gradient="secondary">
          Templates
        </GradientText>
        {templateUseCases.map((template) => (
          <UseCaseLink
            key={template.id}
            id={template.id}
            icon={template.icon}
            text={template.text}
            isTemplate={true}
          />
        ))}
      </TemplateTray>
    </Container>
  );
};

export default BuilderContainer; 